import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { Portal } from "~/helpers/portal";
import { SUBSCRIPTION_LOCK_CONFIG } from "~/helpers/subscriptionLockConfig";
import {
  getSubscriptionLockReason,
  shouldShowSubscriptionLockModal,
} from "~/helpers/subscriptionExpired";
import { useAccount } from "~/hooks/useAccount";
import * as S from "./styles";

export const SubscriptionLockModal = () => {
  const { t } = useTranslation();
  const { account } = useAccount();
  const location = useLocation();
  const navigate = useNavigate();
  const { show: showIntercom } = useIntercom();

  const handlers = useMemo(
    () => ({ navigate, showIntercom }),
    [navigate, showIntercom],
  );

  const lockReason = useMemo(
    () => getSubscriptionLockReason(account),
    [account],
  );

  const isShowing = useMemo(
    () => shouldShowSubscriptionLockModal(account, location.pathname),
    [account, location.pathname],
  );

  if (!isShowing || !lockReason) {
    return null;
  }

  const config = SUBSCRIPTION_LOCK_CONFIG[lockReason];
  const Icon = config.icon;
  const titleId = `subscription-lock-${lockReason}-title`;
  const descriptionId = `subscription-lock-${lockReason}-description`;

  return (
    <Portal>
      <S.Overlay
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}>
        <S.ModalCard>
          <S.IconContainer $background={config.iconBackground}>
            <Icon />
          </S.IconContainer>

          <S.Title id={titleId}>{t(config.titleKey)}</S.Title>

          <S.Description id={descriptionId}>
            {t(config.descriptionKey)}
          </S.Description>

          <S.Actions>
            <S.PrimaryButton
              type="button"
              onClick={() => config.onPrimary(handlers, account)}>
              {t(config.primaryCtaKey)}
            </S.PrimaryButton>

            <S.SecondaryButton
              type="button"
              onClick={() => config.onSecondary(handlers, account)}>
              {t(config.secondaryCtaKey)}
            </S.SecondaryButton>
          </S.Actions>
        </S.ModalCard>
      </S.Overlay>
    </Portal>
  );
};
